import React, { useState } from "react";
import { Box, Link, Popover, Typography } from "@material-ui/core";
import { RouteComponentProps } from "react-router-dom";

import {
  ActionButton,
  B2BSpinner,
  Icon,
  IconName,
  LoadingIndicator,
} from "halifax";
import { ExperienceBadge } from "redmond";
import {
  CardPaymentSelectors,
  getParentState,
  ParentState,
  RewardsPaymentSelectors,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
  WalletSelectors,
} from "@capone/checkout";

import "./styles.scss";
import { Event, TEvent } from "../../state/events";
import { ActivityMachineContext } from "../../state/types";

import { TRIP_TOTAL_BREAKDOWN_TEXT } from "./textConstants";
import { CONFIRM_AND_BOOK_CTA_TEXT } from "../textConstants";
import { formatCurrency } from "../../state/utils";
import { DesktopActivityBookValidationError } from "../DesktopActivityBookWorkflow";
import { CART_UPDATE_TEXT } from "../MobileActivityBookWorkflow/textConstants";
import { EarnInfoBanner, ExperiencesPricingBreakdown } from "../../../common";
import { ActivitySelectors } from "../../state/selectors";
import { PriceBreakdownConnectorProps } from "./container";
import dayjs from "dayjs";

export interface PriceBreakdownProps
  extends RouteComponentProps,
    PriceBreakdownConnectorProps {
  isMobile?: boolean;
  dropdown?: boolean;
  setValidationErrorTypes?: (
    types: DesktopActivityBookValidationError[],
  ) => void;
}

export const PriceBreakdown = ({
  isMobile,
  dropdown,
  largestValueAccount,
  setValidationErrorTypes,
}: PriceBreakdownProps) => {
  const [openPopover, setOpenPopover] = useState<boolean>(false);

  const [state, send] = useCheckoutState<TEvent, ActivityMachineContext>();

  const parentState = getParentState(state.value) as ParentState;

  const activityShop = useSelector(ActivitySelectors.getActivityShop);

  const priceBreakdown = useSelector(
    ActivitySelectors.getActivityPriceBreakdown,
  );

  const isCostCoveredBySelectedPaymentMethods = useSelector(
    CardPaymentSelectors.getIsCostCoveredBySelectedPaymentMethods,
  );

  const rewardsAccounts =
    useSelector(RewardsPaymentSelectors.getRewardsAccounts) || [];
  const selectedRewardsAccountId = useSelector(
    RewardsPaymentSelectors.getSelectedAccountId,
  );
  const selectedPaymentMethodRewardsAccountId = useSelector(
    CardPaymentSelectors.getSelectedPaymentMethodRewardsAccountId,
  );
  const selectedPaymentMethodId = useSelector(
    CardPaymentSelectors.getSelectedPaymentMethodId,
  );
  const isCreditCardPaymentRequired = useSelector(
    CardPaymentSelectors.getIsCreditCardPaymentRequired,
  );
  const cardPaymentAmountRequired = useSelector(
    CardPaymentSelectors.getTotalCardPaymentRequired,
  );
  const selectedPaymentMethod = useSelector(
    CardPaymentSelectors.getSelectedPaymentMethod,
  );

  const creditsAmount = useSelector(WalletSelectors.getCreditAmountToApply);
  const offerToApply = useSelector(WalletSelectors.getSelectedOffer);

  const rewardsPaymentInFiatCurrency = useSelector(
    RewardsPaymentSelectors.getRewardsFiatAmountToApply,
  );
  const rewardsPaymentInRewardsCurrency = useSelector(
    RewardsPaymentSelectors.getRewardsAmountToApply,
  );

  const earnValueByRewardsAccount = useSelector(
    RewardsPaymentSelectors.getEarnValueByRewardsAccount,
  );

  const _earnValue = selectedPaymentMethodRewardsAccountId
    ? earnValueByRewardsAccount[selectedPaymentMethodRewardsAccountId]
    : largestValueAccount?.accountReferenceId
    ? earnValueByRewardsAccount[largestValueAccount?.accountReferenceId]
    : 0;
  void _earnValue; // TODO: wire up earn value display

  const canContinue =
    isMobile ||
    (isCostCoveredBySelectedPaymentMethods && !!selectedRewardsAccountId);

  const handleContinue = () => {
    send({
      type: Event.SET_INITIAL_BOOK_TIME,
      initialTime: dayjs().toISOString(),
    });
    if (canContinue) {
      setValidationErrorTypes?.([]);
      send(Event.NEXT);
    } else {
      setValidationErrorTypes?.(
        (() => {
          switch (true) {
            case !selectedRewardsAccountId:
              return ["rewards"];
            case isCreditCardPaymentRequired && !selectedPaymentMethodId:
              return ["card"];
            default:
              return [];
          }
        })(),
      );
    }
  };

  const togglePopover = () => {
    setOpenPopover((open) => !open);
  };

  const handleClose = () => {
    setOpenPopover(false);
  };

  if (isMobile) {
    if (!priceBreakdown) return null;

    const { totalPriceWithFees } = priceBreakdown;

    const mobileContent = (
      <Box className="activity-price-breakdown-wrapper mobile">
        <ExperiencesPricingBreakdown
          priceBreakdown={priceBreakdown}
          largestValueAccount={largestValueAccount}
          creditsAmount={creditsAmount}
          offerToApply={offerToApply}
          rewardsPaymentInFiatCurrency={rewardsPaymentInFiatCurrency}
          rewardsPaymentInRewardsCurrency={rewardsPaymentInRewardsCurrency}
          cardPaymentAmountRequired={cardPaymentAmountRequired}
          cardPaymentLastFour={selectedPaymentMethod?.last4}
          isAfterCardPaymentStep={[
            ParentState.cardPayment,
            ParentState.review,
            ParentState.cartFulfill,
          ].includes(parentState)}
          showAmountDueLineItem
        />
      </Box>
    );

    return dropdown ? (
      <>
        <Link
          className="activity-price-breakdown-dropdown-root"
          onClick={togglePopover}
          component={"button"}
        >
          <Box className="total-price-label-container">
            <Typography
              className="trip-total"
              variant="subtitle2"
            >{`${TRIP_TOTAL_BREAKDOWN_TEXT}:`}</Typography>
            <Typography className="value fiat" variant="subtitle1">
              {formatCurrency(totalPriceWithFees.fiat)}
            </Typography>
            {/* // TODO update when we start to see rewards */}
            {/* {totalPriceWithFees.rewards && (
              <>
                <Typography className="separator" variant="subtitle2">
                  /
                </Typography>
                <Typography className="value rewards" variant="subtitle2">
                  {formatRewards(totalPriceWithFees.rewards)}
                </Typography>
              </>
            )} */}
          </Box>

          <Icon name={IconName.Dropdown} className="total-price-label-icon" />
        </Link>
        <Popover
          className="activity-checkout-mobile-breakdown-popover"
          open={openPopover}
          PaperProps={{
            className: "price-breakdown-popover-paper",
          }}
          hideBackdrop
          onClose={handleClose}
          onClick={handleClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          disableEnforceFocus
        >
          {mobileContent}
        </Popover>
      </>
    ) : (
      mobileContent
    );
  }
  return (
    <>
      <Box className="activity-price-breakdown-wrapper">
        {[ParentState.cartQuote].includes(parentState) && (
          <Box className="activity-price-breakdown-loading-wrapper">
            <LoadingIndicator
              className="activity-checkout-loading-indicator"
              indicatorSize="small"
              indicator={B2BSpinner}
              message={CART_UPDATE_TEXT}
            />
          </Box>
        )}

        <ExperiencesPricingBreakdown
          priceBreakdown={priceBreakdown}
          largestValueAccount={largestValueAccount}
          isFreeCancellation={
            activityShop
              ? activityShop.badges.length > 0 &&
                activityShop.badges.includes(ExperienceBadge.FreeCancellation)
              : false
          }
          cancellationPolicyProps={{
            selectedDate: priceBreakdown?.date,
            selectedTime: priceBreakdown?.startTime,
            cancellationPolicy: activityShop?.cancellationPolicy,
            startingTimeZone: activityShop?.startingTimeZone,
          }}
          creditsAmount={creditsAmount}
          offerToApply={offerToApply}
          rewardsPaymentInFiatCurrency={rewardsPaymentInFiatCurrency}
          rewardsPaymentInRewardsCurrency={rewardsPaymentInRewardsCurrency}
          cardPaymentAmountRequired={cardPaymentAmountRequired}
          cardPaymentLastFour={selectedPaymentMethod?.last4}
          isAfterCardPaymentStep={[
            ParentState.cardPayment,
            ParentState.review,
            ParentState.cartFulfill,
          ].includes(parentState)}
          showAmountDueLineItem
        />

        {[ParentState.cardPayment, ParentState.cartFulfill].includes(
          parentState,
        ) && (
          <ActionButton
            onClick={handleContinue}
            message={CONFIRM_AND_BOOK_CTA_TEXT}
            className="activity-continue-button book price-breakdown"
            disabled={
              parentState === ParentState.cartFulfill ||
              !isCostCoveredBySelectedPaymentMethods
            }
          />
        )}
      </Box>
      {[
        ParentState.cardPayment,
        ParentState.review,
        ParentState.cartFulfill,
      ].includes(parentState) ? (
        isCreditCardPaymentRequired && (
          <EarnInfoBanner
            largestValueAccount={
              rewardsAccounts.find(
                ({ accountReferenceId }) =>
                  accountReferenceId === selectedPaymentMethodRewardsAccountId,
              ) || largestValueAccount
            }
            priceBreakdown={priceBreakdown}
            cardPaymentAmountRequired={cardPaymentAmountRequired}
            earnValueByRewardsAccount={earnValueByRewardsAccount}
          />
        )
      ) : (
        <EarnInfoBanner
          largestValueAccount={largestValueAccount}
          priceBreakdown={priceBreakdown}
        />
      )}
    </>
  );
};
